<template>
  <div class="hero-banner critical-component-clear"
   :class="isMobile
    ? `${banner.alignHorizontalMobileValue || ''}`
    : `${banner.alignHorizontalDesktopValue || ''}`"
  >
    <div
      class="hero-banner__container"
      :class=" [
        `text-colour--${banner.heading.textColour}`,
        layoutClass,
        settings.headingWidth === 'full' ? 'full' : 'default',
        settings.background === 'no_background' ? 'no-background' : 'default_background'
      ]"
      role="link"
      tabindex="0"
      @click="handleBannerClick"
    >
      <div class="hero-banner__media">
        <template v-if="banner.mobileVideo || banner.desktopVideo">
          <video-player
            v-if="banner.mobileVideo"
            class="hero-banner__mobile-video"
            :attributes="banner.videoSettings"
            :image="banner.mobileVideo.preview_image"
            :lazyload="banner.lazyload"
            :loaded-animation="false"
            :ratio="false"
            :intersection-observer="{
              enable: banner.videoSettings.autoplay,
            }"
            sizes="100vw"
            :sources="banner.mobileVideo.sources"
          />

          <video-player
            v-if="banner.desktopVideo"
            class="hero-banner__desktop-video"
            :attributes="banner.videoSettings"
            :image="banner.desktopVideo.preview_image"
            :lazyload="banner.lazyload"
            :loaded-animation="false"
            :ratio="settings.ratioDesktop"
            :intersection-observer="{
              enable: banner.videoSettings.autoplay,
            }"
            sizes="100vw"
            :sources="banner.desktopVideo.sources"
          />
        </template>

        <!-- Additional Mobile/Desktop Video -->
        <template v-if="banner.additionalMobileVideo || banner.additionalDesktopVideo">
          <video-player
            v-if="banner.additionalMobileVideo"
            class="hero-banner__additional-mobile-video"
            :attributes="banner.videoSettings"
            :image="banner.additionalMobileVideo.preview_image"
            :lazyload="banner.lazyload"
            :loaded-animation="false"
            :ratio="false"
            :intersection-observer="{ enable: banner.videoSettings.autoplay }"
            sizes="100vw"
            :sources="banner.additionalMobileVideo.sources"
          />
          <video-player
            v-if="banner.additionalDesktopVideo"
            class="hero-banner__additional-desktop-video"
            :attributes="banner.videoSettings"
            :image="banner.additionalDesktopVideo.preview_image"
            :lazyload="banner.lazyload"
            :loaded-animation="false"
            :ratio="settings.ratioDesktop"
            :intersection-observer="{ enable: banner.videoSettings.autoplay }"
            sizes="100vw"
            :sources="banner.additionalDesktopVideo.sources"
          />
        </template>

        <template
          v-if="banner.mobileImage?.url ||
            banner.desktopImage?.url ||
            banner.additionalMobileImage?.url ||
            banner.additionalDesktopImage?.url
          "
        >
          <art-direction
            v-if="banner.mobileImage?.url && !banner.mobileVideo"
            class="hero-banner__mobile-image"
            :image="banner.mobileImage"
            :image-desktop="banner.mobileImage"
            :ratio="settings.ratio"
            :ratio-desktop="settings.ratio"
            :loaded-animation="false"
            :lazyload="banner.lazyload"
          />
          <art-direction
            v-if="banner.desktopImage?.url && !banner.desktopVideo"
            class="hero-banner__desktop-image"
            :image="banner.desktopImage"
            :image-desktop="banner.desktopImage"
            :ratio="settings.ratioDesktop"
            :ratio-desktop="settings.ratioDesktop"
            :loaded-animation="false"
            :lazyload="banner.lazyload"
          />
          <!-- Additional Mobile Image -->
          <art-direction
            v-if="banner.additionalMobileImage?.url"
            class="hero-banner__additional-mobile-image"
            :image="banner.additionalMobileImage"
            :ratio="settings.ratio"
            :ratio-desktop="settings.ratio"
            :loaded-animation="false"
            :lazyload="banner.lazyload"
          />
          <!-- Additional Desktop Image -->
          <art-direction
            v-if="banner.additionalDesktopImage?.url && !banner.additionalDesktopVideo"
            class="hero-banner__additional-desktop-image"
            :image="banner.additionalDesktopImage"
            :image-desktop="banner.additionalDesktopImage"
            :ratio="settings.ratioDesktop"
            :ratio-desktop="settings.ratioDesktop"
            :loaded-animation="false"
            :lazyload="banner.lazyload"
          />
        </template>

        <div
          v-if="banner.enableOverlay"
          class="hero-banner__overlay"
          :style="`background-color: rgba(0,0,0,${banner.overlayRange})`"
        />
      </div>

      <div
        class="hero-banner__content grid"
      >
        <div
          :class="isMobile
            ? banner.alignVerticalMobile
            : banner.alignVerticalDesktop
          "
        >
          <heading-copy
            :class="isMobile
              ? banner.alignHorizontalMobile
              : banner.alignHorizontalDesktop
            "
            :classes="{
              copy: 'text-body-s-regular text-body-m-regular-desktop',
            }"
            :content="headingContent"
            :style="{'--color-primary': settings.headingBackgroundColor }"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Hero banner (hero-banner)
 * -----------------------------------------------------------------------------
 * Section featuring a full width image or video and overlayed text content.
 * - Dynamic section.
 *
 * @param {Object} banner - Section banner.
 * @param {String} settings.sectionId - Section's Liquid ID.
 * @param {String} settings.ratio - Image's Ratio on Mobile devices.
 * @param {String} settings.ratioDesktop - Image's Ratio on Destkop devices.
 *
 */
import ArtDirection from '~global/images/art-direction'
import HeadingCopy from '~async/heading-copy/heading-copy'
import VideoPlayer from '~async/video-player/video-player'
import { mapState } from 'vuex'

export default {
  name: 'HeroBanner',

  components: {
    ArtDirection,
    HeadingCopy,
    VideoPlayer,
  },

  props: {
    settings: {
      type: Object,
      default: () => ({}),
    },
    banner: {
      type: Object,
      default: () => ({}),
    },
  },

  computed: {
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),
    layoutType() {
      return this.isMobile
        ? this.settings.layoutMobile || 'existing_layout'
        : this.settings.layoutDesktop || 'existing_layout'
    },
    layoutClass() {
      return this.isMobile
        ? `layout--mobile ${this.layoutType}`
        : `layout--desktop ${this.layoutType}`
    },

    /**
     * Heading content based on device type (mobile/desktop) set the values
     */
    headingContent() {
      const desc = this.banner.heading || {}
      const isMobile = this.isMobile
      return {
        ...desc,
        buttonText: isMobile ? desc.buttonTextMobile : desc.buttonTextDesktop,
        buttonUrl: isMobile ? desc.buttonUrlMobile : desc.buttonUrlDesktop,
        buttonStyle: isMobile ? desc.buttonStyleMobile : desc.buttonStyleDesktop,
        buttonMode: isMobile ? desc.buttonModeMobile : desc.buttonModeDesktop,
        secondButtonText: isMobile ? desc.secondButtonTextMobile : desc.secondButtonTextDesktop,
        secondButtonUrl: isMobile ? desc.secondButtonUrlMobile : desc.secondButtonUrlDesktop,
        secondButtonStyle: isMobile ? desc.secondButtonStyleMobile : desc.secondButtonStyleDesktop,
        secondButtonMode: isMobile ? desc.secondButtonModeMobile : desc.secondButtonModeDesktop,
      }
    },
  },
  methods: {
    handleBannerClick(event) {
      if (
        event.target.closest('.video-player__controls') ||
        event.target.classList.contains('video-player__control')
      ) {
        return
      }
      if (this.banner.heading.headingLink) {
        window.location.href = this.banner.heading.headingLink
      }
    },
  },
}
</script>

<style lang="scss">
@import './hero-banner';
@import '../video-player/video-player';
</style>
